// Services
import CSessao from '../../CSessao';
import CIndexedDB from '../../CIndexedDB';

// Components
import type { TOrdenacaoTabela } from '@/components/dados/RenderizadorTabela.vue';
import type {
  TConfigQuantidadeExibida,
  TOrdemDadosGrafico,
  TTipoGraficoInterno,
} from '@/components/dados/apex/ApexCharts';
import type { TControlesAdicionaisDataSet, TPersonalizacaoDataSet } from '@/components/dados/DataSet.vue';

// Tipagem
/**
 * Tipo final esperado:
 * {
 *    tipo: 'Tabela' | 'Grafico',
 *    ...camposEspecificos de acordo com "tipo",
 *    personalizacao?: {
 *                        controlesAdicionais: { titulo: string, valor: any }[],
 *                        configuracoesAdicionais?: Record<string, any>,
 *                     }
 * }
 */
export type TPersonalizacaoPreferenciasDataSet = Omit<TPersonalizacaoDataSet, 'controlesAdicionais'> & {
  controlesAdicionais: Omit<TControlesAdicionaisDataSet, 'items'>[];
};
export type TPreferenciasDataSet<T extends 'Tabela' | 'Grafico'> = {
  tipo: T;
} & { personalizacao?: TPersonalizacaoPreferenciasDataSet } & (T extends 'Tabela'
    ? TPreferenciasDataSetTabela
    : T extends 'Grafico'
      ? TPreferenciasDataSetGrafico
      : never);

export type TPreferenciasDataSetGrafico = {
  exibicao: TTipoGraficoInterno;
  ordenacao?: TOrdemDadosGrafico;
  quantidadeExibida?: TConfigQuantidadeExibida;
};

export type TPreferenciasDataSetTabela = {
  ordenacao: TOrdenacaoTabela[];
};

export default class CPreferenciasDataSet extends CIndexedDB<string> {
  constructor(pIndexDB: IDBDatabase, pStoreName: string) {
    super(pIndexDB, pStoreName);
  }

  private setIdsDataSets?: Set<string>;

  /**
   * Método criado para alertar desenvolvedores de datasets com ID's duplicados
   */
  public registrarIdDataSetParaModoDev(id: string) {
    if (!import.meta.env.DEV) return;
    if (!this.setIdsDataSets) {
      this.setIdsDataSets = new Set();
    }

    if (id && this.setIdsDataSets.has(id))
      throw new Error(`Já existe dataset de ID ${id} em ${this.storeName}.
                      Cada id deve ser único por dashboard`);

    this.setIdsDataSets.add(id);
  }

  public removerIdDataSetParaModoDev(id: string) {
    if (!import.meta.env.DEV) return;
    if (!this.setIdsDataSets) return;
    this.setIdsDataSets.delete(id);
  }

  private getChave(pIdenfificador: string) {
    return `${pIdenfificador}_${CSessao.sessaoAtual.obterUsuario().codigo}`;
  }

  private isPreferenciaTabela(value: unknown): value is TPreferenciasDataSet<'Tabela'> {
    if (typeof value !== 'object' || value === null) return false;

    return 'tipo' in value && value.tipo === 'Tabela';
  }

  private isPreferenciaGrafico(value: unknown): value is TPreferenciasDataSet<'Grafico'> {
    if (typeof value !== 'object' || value === null) return false;

    return 'tipo' in value && value.tipo === 'Grafico';
  }

  public async armazenarPreferencias<E extends 'Tabela' | 'Grafico'>(
    pIdentificador: string,
    pDados: TPreferenciasDataSet<E>,
  ) {
    try {
      await this.armazenar(this.getChave(pIdentificador), pDados);
    } catch (error) {
      this.loggarErro(pIdentificador, String(error));
    }
  }

  public async recuperarPreferencias(pIdentificador: string) {
    try {
      const valorRecuperado = await this.recuperar<unknown>(this.getChave(pIdentificador));
      if (this.isPreferenciaGrafico(valorRecuperado)) return valorRecuperado;
      if (this.isPreferenciaTabela(valorRecuperado)) return valorRecuperado;

      return null;
    } catch (error) {
      this.loggarErro(pIdentificador, String(error));
    }
  }

  public async excluirPreferencias(pIdentificador: string) {
    try {
      await this.excluir(this.getChave(pIdentificador));
    } catch (error) {
      this.loggarErro(pIdentificador, String(error));
    }
  }

  /**
   * Remove sessões de abas que não foram atualizadas há mais de X horas.
   * @param pHorasLimite Tempo em horas para expiração (default 24h)
   */
  async realizarLimpezaSessoesInativas(pHorasLimite: number = 24): Promise<void> {
    try {
      const transaction = this.database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // getAll() recupera todos os objetos do Store de uma vez
      const request = store.getAll();

      request.onsuccess = () => {
        const todosRegistros = request.result;
        const agora = new Date().getTime();
        const limiteMs = pHorasLimite * 60 * 60 * 1000; // Converte horas para milissegundos

        todosRegistros.forEach((registro: any) => {
          const timestampRegistro = registro.timestamp;

          // Se o tempo não existir ou for menor que o limite de expiração, deletamos
          if (timestampRegistro && agora - timestampRegistro > limiteMs) {
            console.log(`[GC PreferenciasDataset] Removendo sessão expirada: ${registro.key}`);
            store.delete(registro.key);
          }
        });
      };

      transaction.oncomplete = () => {
        //console.log(`[GC PreferenciasDataset] Processo de limpeza concluído.`);
      };
    } catch (error) {
      console.error('[GC PreferenciasDataset] Erro ao realizar limpeza:', error);
    }
  }
}
